// src/contexts/EmployerContext.tsx
// FIXED: Use 1-indexed pagination + removed duplicateNeed (use createNeed instead)
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { EmployerService } from '@/services/EmployerService';
import { NeedService } from '@/services/NeedService';
import {
    EmployerWithDetails,
    UpdateEmployerRequest,
    UpdateUnitRequest
} from '@/lib/models/employer';
import {
    Need,
    CreateNeedRequest,
    UpdateNeedRequest,
    NeedStatus
} from '@/lib/models/need';

interface EmployerContextType {
    // Profile & Unit
    employerDetails: EmployerWithDetails | null;
    isLoadingProfile: boolean;
    isSavingProfile: boolean;
    profileError: string | null;

    getEmployerProfile: () => Promise<EmployerWithDetails | null>;
    updateEmployerProfile: (data: UpdateEmployerRequest) => Promise<void>;
    updateUnit: (data: UpdateUnitRequest) => Promise<void>;
    refreshEmployerProfile: () => Promise<void>;

    isProfileComplete: boolean;
    profileCompletionPercentage: number;

    // Needs
    activeNeeds: Need[];
    isLoadingNeeds: boolean;
    needError: string | null;
    clearNeedError: () => void;

    getMyActiveNeeds: (page?: number, limit?: number) => Promise<void>;
    getNeedById: (needId: string) => Promise<Need | null>;
    createNeed: (data: CreateNeedRequest) => Promise<Need | null | void>;
    updateNeed: (needId: string, data: UpdateNeedRequest) => Promise<Need | null>;
    updateNeedStatus: (needId: string, status: NeedStatus) => Promise<Need | null>;
    deleteNeed: (needId: string) => Promise<boolean>;

    selectedNeed: Need | null;
    setSelectedNeed: (need: Need | null) => void;

    refreshAllEmployerData: () => Promise<void>;
}

const EmployerContext = createContext<EmployerContextType | undefined>(undefined);

export const EmployerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, isAuthenticated } = useAuth();

    const [employerDetails, setEmployerDetails] = useState<EmployerWithDetails | null>(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(false);
    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);

    const [activeNeeds, setActiveNeeds] = useState<Need[]>([]);
    const [isLoadingNeeds, setIsLoadingNeeds] = useState(false);
    const [needError, setNeedError] = useState<string | null>(null);

    const [selectedNeed, setSelectedNeed] = useState<Need | null>(null);

    useEffect(() => {
        if (isAuthenticated && user?.id) {
            if (user.role === 'employer') {
                console.log('💼 [Employer] Employer user authenticated, loading all data...');
                loadAllEmployerData();
            } else {
                console.log(`💼 [Employer] Non-employer user (${user.role}) detected, skipping data load`);
                clearAllEmployerData();
            }
        } else {
            console.log('💼 [Employer] User not authenticated, clearing data...');
            clearAllEmployerData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, user?.id, user?.role]);

    const getEmployerProfile = useCallback(async (): Promise<EmployerWithDetails | null> => {
        if (!user) return null;
        if (user.role !== 'employer') return null;

        setIsLoadingProfile(true);
        setProfileError(null);

        try {
            const details = await EmployerService.getMyEmployerDetails();
            if (details) {
                setEmployerDetails(details);
                return details;
            } else {
                setEmployerDetails(null);
                return null;
            }
        } catch (error: any) {
            if (error?.statusCode === 404) {
                setEmployerDetails(null);
                setProfileError(null);
                return null;
            }
            setProfileError('Failed to load employer profile');
            return null;
        } finally {
            setIsLoadingProfile(false);
        }
    }, [user]);

    const updateEmployerProfile = useCallback(async (data: UpdateEmployerRequest): Promise<void> => {
        if (!employerDetails || user?.role !== 'employer') {
            throw new Error('Employer profile not loaded');
        }

        setIsSavingProfile(true);
        setProfileError(null);

        try {
            await EmployerService.updateEmployer(employerDetails.employer.id, data);
            await getEmployerProfile();
        } catch (error: any) {
            setProfileError(error?.message || 'Failed to update profile');
            throw error;
        } finally {
            setIsSavingProfile(false);
        }
    }, [employerDetails, user?.role, getEmployerProfile]);

    const updateUnit = useCallback(async (data: UpdateUnitRequest): Promise<void> => {
        if (!employerDetails || user?.role !== 'employer') {
            throw new Error('Employer profile not loaded');
        }

        setIsSavingProfile(true);
        setProfileError(null);

        try {
            await EmployerService.updateUnit(employerDetails.unit.id, data);
            await getEmployerProfile();
        } catch (error: any) {
            setProfileError(error?.message || 'Failed to update unit');
            throw error;
        } finally {
            setIsSavingProfile(false);
        }
    }, [employerDetails, user?.role, getEmployerProfile]);

    const refreshEmployerProfile = useCallback(async (): Promise<void> => {
        await getEmployerProfile();
    }, [getEmployerProfile]);

    const clearNeedError = useCallback(() => setNeedError(null), []);

    // ✅ FIXED: Default to page=1 (1-indexed pagination)
    const getMyActiveNeeds = useCallback(async (page: number = 1, limit: number = 20): Promise<void> => {
        if (user?.role !== 'employer') return;

        setIsLoadingNeeds(true);
        setNeedError(null);

        try {
            const result = await NeedService.getMyActiveNeeds(page, limit);
            setActiveNeeds(result.needs);
        } catch (error: any) {
            setNeedError(error?.message || 'Failed to load needs');
        } finally {
            setIsLoadingNeeds(false);
        }
    }, [user?.role]);

    const getNeedById = useCallback(async (needId: string): Promise<Need | null> => {
        if (user?.role !== 'employer') return null;

        try {
            const need = await NeedService.getNeedById(needId);
            return need;
        } catch (error: any) {
            setNeedError(error?.message || 'Failed to load need');
            return null;
        }
    }, [user?.role]);

    const createNeed = useCallback(async (data: CreateNeedRequest): Promise<Need | null> => {
        if (user?.role !== 'employer') return null;

        const validation = NeedService.validateCreateNeedRequest(data);
        if (!validation.valid) {
            setNeedError(validation.errors.join(', '));
            return null;
        }

        setIsLoadingNeeds(true);
        setNeedError(null);

        try {
            const newNeed = await NeedService.createNeed(data);
            await getMyActiveNeeds();
            return newNeed;
        } catch (error: any) {
            setNeedError(error?.message || 'Failed to create need');
            throw error;
        } finally {
            setIsLoadingNeeds(false);
        }
    }, [user?.role, getMyActiveNeeds]);

    const updateNeed = useCallback(async (
        needId: string,
        data: UpdateNeedRequest
    ): Promise<Need | null> => {
        if (user?.role !== 'employer') return null;

        setIsLoadingNeeds(true);
        setNeedError(null);

        try {
            const need = await NeedService.updateNeed(needId, data);
            await getMyActiveNeeds();

            if (selectedNeed?.id === needId) {
                setSelectedNeed(need);
            }

            return need;
        } catch (error: any) {
            setNeedError(error?.message || 'Failed to update need');
            return null;
        } finally {
            setIsLoadingNeeds(false);
        }
    }, [user?.role, selectedNeed?.id, getMyActiveNeeds]);

    const updateNeedStatus = useCallback(async (
        needId: string,
        status: NeedStatus
    ): Promise<Need | null> => {
        if (user?.role !== 'employer') return null;

        setIsLoadingNeeds(true);
        setNeedError(null);

        try {
            const need = await NeedService.updateNeedStatus(needId, status);
            await getMyActiveNeeds();

            if (selectedNeed?.id === needId) {
                setSelectedNeed(need);
            }

            return need;
        } catch (error: any) {
            setNeedError(error?.message || 'Failed to update status');
            return null;
        } finally {
            setIsLoadingNeeds(false);
        }
    }, [user?.role, selectedNeed?.id, getMyActiveNeeds]);

    const deleteNeed = useCallback(async (needId: string): Promise<boolean> => {
        if (user?.role !== 'employer') return false;

        setIsLoadingNeeds(true);
        setNeedError(null);

        try {
            await NeedService.deleteNeed(needId);
            await getMyActiveNeeds();

            if (selectedNeed?.id === needId) {
                setSelectedNeed(null);
            }

            return true;
        } catch (error: any) {
            setNeedError(error?.message || 'Failed to delete need');
            return false;
        } finally {
            setIsLoadingNeeds(false);
        }
    }, [user?.role, selectedNeed?.id, getMyActiveNeeds]);

    const loadAllEmployerData = useCallback(async () => {
        await Promise.all([
            getEmployerProfile(),
            getMyActiveNeeds()
        ]);
    }, [getEmployerProfile, getMyActiveNeeds]);

    const clearAllEmployerData = useCallback(() => {
        setEmployerDetails(null);
        setActiveNeeds([]);
        setSelectedNeed(null);
    }, []);

    const refreshAllEmployerData = useCallback(async (): Promise<void> => {
        await loadAllEmployerData();
    }, [loadAllEmployerData]);

    const isProfileComplete = employerDetails
        ? EmployerService.isEmployerProfileComplete(employerDetails)
        : false;

    const profileCompletionPercentage = employerDetails
        ? EmployerService.getProfileCompletionPercentage(employerDetails)
        : 0;

    const value: EmployerContextType = {
        employerDetails,
        isLoadingProfile,
        isSavingProfile,
        profileError,
        getEmployerProfile,
        updateEmployerProfile,
        updateUnit,
        refreshEmployerProfile,
        isProfileComplete,
        profileCompletionPercentage,
        activeNeeds,
        isLoadingNeeds,
        needError,
        clearNeedError,
        getMyActiveNeeds,
        getNeedById,
        createNeed,
        updateNeed,
        updateNeedStatus,
        deleteNeed,
        selectedNeed,
        setSelectedNeed,
        refreshAllEmployerData,
    };

    return (
        <EmployerContext.Provider value={value}>
            {children}
        </EmployerContext.Provider>
    );
};

export const useEmployer = (): EmployerContextType => {
    const context = useContext(EmployerContext);
    if (context === undefined) {
        throw new Error('useEmployer must be used within an EmployerProvider');
    }
    return context;
};